// Nuso Admin — back-office control room
const { useState, useEffect, useRef, useCallback } = React;

// ── Helpers ───────────────────────────────────────────────────────────────────
function cn(...classes) { return classes.filter(Boolean).join(' '); }

function Btn({ variant = 'default', size = 'default', className = '', children, ...props }) {
  const base = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 cursor-pointer';
  const variants = {
    default: 'border border-slate-200 bg-white hover:bg-slate-50 text-slate-700',
    primary: 'bg-[hsl(138,26%,46%)] hover:bg-[hsl(138,26%,38%)] text-white border-transparent',
    danger: 'border border-red-200 text-red-600 hover:bg-red-50',
    ghost: 'hover:bg-slate-100 text-slate-600 border-transparent',
  };
  const sizes = { default: 'h-9 px-4 py-2', sm: 'h-8 px-3 text-xs', xs: 'h-7 px-2.5 text-xs' };
  return <button className={cn(base, variants[variant], sizes[size], className)} {...props}>{children}</button>;
}

function Badge({ variant = 'default', className = '', ...props }) {
  const variants = {
    default: 'bg-[hsl(138,26%,94%)] text-[hsl(138,26%,30%)] border-[hsl(138,26%,75%)]',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    grey: 'bg-slate-100 text-slate-500 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold', variants[variant], className)} {...props} />;
}

function Card({ className = '', ...props }) {
  return <div className={cn('rounded-xl border border-slate-200 bg-white shadow-sm', className)} {...props} />;
}

function Spinner({ className = '' }) {
  return <span className={cn('inline-block w-4 h-4 border-2 border-slate-200 border-t-[hsl(138,26%,46%)] rounded-full animate-spin', className)} />;
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 transition-all hover:border-slate-300 hover:shadow-sm">
      <div className="text-[0.6875rem] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-bold leading-none mb-1 text-slate-900">{value}</div>
      {sub && <div className="text-[0.75rem] font-medium text-slate-400">{sub}</div>}
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center h-48 gap-3 text-slate-400">
      <Spinner className="w-6 h-6" />
    </div>
  );
}

// ── API ───────────────────────────────────────────────────────────────────────
const API = '';
function api(path, opts = {}) {
  const token = localStorage.getItem('admin_token');
  return fetch(API + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
  });
}

function fmt(pence, currency = 'gbp') {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: 0 }).format(pence / 100);
}
// Always parse to number first so DB strings like "011269" format correctly
function fmtN(n) { return Number(n || 0).toLocaleString('en-GB'); }
function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function timeAgo(ts) {
  const diff = Date.now() - (typeof ts === 'number' ? ts * 1000 : new Date(ts).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
// Gemini 2.0 Flash pricing: $0.075/1M input · $0.30/1M output
// When only total tokens known, assume 70/30 split → ~$0.14/1M blended
const GEMINI_INPUT_PER_M  = 0.075;
const GEMINI_OUTPUT_PER_M = 0.30;
function geminiCost(promptTokens = 0, outputTokens = 0) {
  return (Number(promptTokens) / 1e6 * GEMINI_INPUT_PER_M) +
         (Number(outputTokens) / 1e6 * GEMINI_OUTPUT_PER_M);
}
function geminiCostFromTotal(totalTokens = 0) {
  const t = Number(totalTokens);
  return (t * 0.7 / 1e6 * GEMINI_INPUT_PER_M) + (t * 0.3 / 1e6 * GEMINI_OUTPUT_PER_M);
}
function fmtCost(dollars) {
  if (dollars === 0) return '$0.00';
  if (dollars < 0.0005) return '<$0.001';
  if (dollars < 0.01) return `$${dollars.toFixed(4)}`;
  if (dollars < 1) return `$${dollars.toFixed(3)}`;
  return `$${dollars.toFixed(2)}`;
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Platform',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
      { id: 'clients', label: 'Clients', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
      { id: 'insights', label: 'AI Insights', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      { id: 'support', label: 'Support', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { id: 'stripe', label: 'Stripe', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { id: 'apikeys', label: 'API Keys', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
      { id: 'provision', label: 'Provision', icon: 'M12 4v16m8-8H4' },
    ],
  },
];

// Flatten for legacy lookups
const NAV = NAV_GROUPS.flatMap(g => g.items);

function NavIcon({ d }) {
  return (
    <svg width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d={d} />
    </svg>
  );
}

function Sidebar({ page, setPage, user, onLogout, supportUnread }) {
  const initials = (user?.name || user?.email || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="w-[240px] min-w-[240px] bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0">
      {/* Logo */}
      <div className="px-4 h-[60px] flex items-center gap-3 border-b border-slate-200 shrink-0">
        <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
          <rect width="32" height="32" rx="7" fill="hsl(138,26%,46%)"/>
          <text x="50%" y="50%" fontSize="18" fontWeight="800" fontFamily="system-ui,sans-serif" fill="white" textAnchor="middle" dominantBaseline="central">N</text>
        </svg>
        <div>
          <div className="text-[0.875rem] font-semibold text-slate-900 leading-none">Nuso</div>
          <div className="text-[0.68rem] text-slate-400 mt-0.5 font-medium">Control Room</div>
        </div>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV_GROUPS.map(group => (
          <div key={group.label}>
            <div className="px-2 mb-1.5 text-[0.68rem] font-semibold uppercase tracking-widest text-slate-400 select-none">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map(n => {
                const active = page === n.id;
                return (
                  <button
                    key={n.id}
                    onClick={() => setPage(n.id)}
                    className={cn(
                      'flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-left text-sm transition-colors duration-100',
                      active
                        ? 'bg-[hsl(138,26%,94%)] text-[hsl(138,26%,32%)] font-medium'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 font-normal'
                    )}
                  >
                    <NavIcon d={n.icon} />
                    <span className="flex-1">{n.label}</span>
                    {n.id === 'support' && supportUnread > 0 && (
                      <span className="bg-red-500 text-white rounded-full text-[0.62rem] px-1.5 py-px font-bold leading-none">{supportUnread}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer — sidebar-07 style */}
      <div className="px-3 py-3 border-t border-slate-200 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors group">
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
            <circle cx="16" cy="16" r="16" fill="hsl(138,26%,46%)"/>
            <text x="50%" y="50%" fontSize="12" fontWeight="700" fontFamily="system-ui,sans-serif" fill="white" textAnchor="middle" dominantBaseline="central">{initials}</text>
          </svg>
          <div className="flex-1 min-w-0">
            <div className="text-[0.8rem] font-semibold text-slate-900 truncate leading-tight">{user?.name || 'Admin'}</div>
            <div className="text-[0.7rem] text-slate-400 truncate leading-tight">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            title="Sign out"
            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 rounded cursor-pointer"
          >
            <svg width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard Page ────────────────────────────────────────────────────────────
function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/admin/stats').then(r => r.json()),
      api('/api/admin/stripe/overview').then(r => r.json()),
    ]).then(([s, st]) => {
      setStats(s);
      setStripe(st);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  return (
    <div>
      <div className="text-2xl font-bold tracking-tight">Dashboard</div>
      <div className="text-sm text-slate-500 mb-6">Platform-wide overview</div>

      <div className="grid gap-3.5 mb-8" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        <StatCard label="Client Orgs" value={stats?.org_count ?? '—'} sub="Total organisations" />
        <StatCard label="Total Users" value={stats?.user_count ?? '—'} sub="Across all orgs" />
        <StatCard label="MRR" value={stripe?.error ? '—' : fmt(stripe?.mrr || 0, stripe?.currency)} sub="Monthly recurring" />
        <StatCard label="ARR" value={stripe?.error ? '—' : fmt(stripe?.arr || 0, stripe?.currency)} sub="Annualised" />
        <StatCard label="Active Subs" value={stripe?.error ? '—' : stripe?.active_subscriptions ?? '—'} sub="Live subscriptions" />
        <StatCard label="30d Revenue" value={stripe?.error ? '—' : fmt(stripe?.revenue_30d || 0, stripe?.currency)} sub={`${stripe?.failed_charges_30d || 0} failed charges`} />
      </div>

      {stripe?.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3.5 text-red-600 text-sm mb-6">
          Stripe not configured — add STRIPE_SECRET_KEY to .env
        </div>
      )}

      <RecentActivity />
    </div>
  );
}

function RecentActivity() {
  const [charges, setCharges] = useState([]);
  useEffect(() => {
    api('/api/admin/stripe/charges').then(r => r.json()).then(d => setCharges(d.charges || [])).catch(() => {});
  }, []);

  if (!charges.length) return null;

  return (
    <div>
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Recent Charges</div>
      <Card className="overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Description</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">When</th>
            </tr>
          </thead>
          <tbody>
            {charges.slice(0, 10).map(c => (
              <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-slate-800">{c.customer_name || '—'}</div>
                  <div className="text-xs text-slate-500">{c.customer_email || ''}</div>
                </td>
                <td className="px-4 py-3 font-semibold text-sm">{fmt(c.amount, c.currency)}</td>
                <td className="px-4 py-3">
                  <Badge variant={c.status === 'succeeded' ? 'green' : c.status === 'failed' ? 'red' : 'grey'}>{c.status}</Badge>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate">{c.description || '—'}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{timeAgo(c.created)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── Clients Page ──────────────────────────────────────────────────────────────
function ClientsPage({ onViewDetail }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [orgStats, setOrgStats] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [inviteModal, setInviteModal] = useState(null);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignRole, setAssignRole] = useState('admin');
  const [assignMsg, setAssignMsg] = useState('');
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announceTitle, setAnnounceTitle] = useState('');
  const [announceBody, setAnnounceBody] = useState('');
  const [announceSending, setAnnounceSending] = useState(false);
  const [announceMsg, setAnnounceMsg] = useState('');

  function loadOrgs() {
    setLoading(true);
    api('/api/admin/orgs').then(r => r.json()).then(d => { setOrgs(d.orgs || []); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { loadOrgs(); }, []);

  function expand(org) {
    if (expanded === org.id) { setExpanded(null); return; }
    setExpanded(org.id);
    if (!orgStats[org.id]) {
      api(`/api/admin/orgs/${org.id}/stats`).then(r => r.json()).then(d => setOrgStats(p => ({ ...p, [org.id]: d })));
    }
  }

  async function toggleActive(org) {
    await api(`/api/admin/orgs/${org.id}`, { method: 'PUT', body: JSON.stringify({ active: org.active ? 0 : 1 }) });
    loadOrgs();
  }

  async function deleteOrg(org) {
    await api(`/api/admin/orgs/${org.id}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    setExpanded(null);
    loadOrgs();
  }

  async function refreshInvite(org) {
    const r = await api(`/api/admin/orgs/${org.id}/invite`, { method: 'POST' });
    const d = await r.json();
    setInviteModal(d);
  }

  async function sendAnnouncement() {
    if (!announceTitle.trim()) return;
    setAnnounceSending(true);
    setAnnounceMsg('');
    try {
      const r = await api('/api/admin/announcements', { method: 'POST', body: JSON.stringify({ title: announceTitle.trim(), body: announceBody.trim() || null }) });
      const d = await r.json();
      if (d.success) {
        setAnnounceMsg('✓ Sent to all organisations');
        setAnnounceTitle('');
        setAnnounceBody('');
        setTimeout(() => { setAnnounceMsg(''); setShowAnnounce(false); }, 2500);
      } else {
        setAnnounceMsg('✗ ' + (d.error || 'Failed'));
      }
    } catch { setAnnounceMsg('✗ Network error'); }
    setAnnounceSending(false);
  }

  async function assignUser(orgId) {
    setAssignMsg('');
    const r = await api(`/api/admin/orgs/${orgId}/assign-user`, { method: 'POST', body: JSON.stringify({ email: assignEmail, role: assignRole }) });
    const d = await r.json();
    if (!r.ok) { setAssignMsg(d.error); return; }
    setAssignEmail('');
    setAssignMsg('✓ Assigned');
    api(`/api/admin/orgs/${orgId}/stats`).then(r => r.json()).then(d => setOrgStats(p => ({ ...p, [orgId]: d })));
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-2xl font-bold tracking-tight">Clients</div>
          <div className="text-sm text-slate-500">All organisations on the platform</div>
        </div>
        <Btn onClick={() => { setShowAnnounce(true); setAnnounceMsg(''); }}>
          📣 Announce
        </Btn>
      </div>

      {/* Announce Modal */}
      {showAnnounce && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowAnnounce(false)}>
          <div className="bg-white rounded-xl p-8 max-w-[480px] w-[90%] shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">Send Announcement</h2>
            <p className="text-slate-400 text-sm mb-5">Sends an in-app notification to all organisations.</p>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Title *</label>
              <input
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
                placeholder="e.g. New feature: MMM Experiment Bed"
                value={announceTitle}
                onChange={e => setAnnounceTitle(e.target.value)}
              />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Body (optional)</label>
              <textarea
                className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)] min-h-[80px] resize-y"
                placeholder="Describe what's new or changed..."
                value={announceBody}
                onChange={e => setAnnounceBody(e.target.value)}
              />
            </div>
            {announceMsg && (
              <div className={cn(
                'px-3 py-2 rounded-md mb-4 text-sm',
                announceMsg.startsWith('✓') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'
              )}>
                {announceMsg}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Btn onClick={() => setShowAnnounce(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={sendAnnouncement} disabled={announceSending || !announceTitle.trim()}>
                {announceSending ? <><Spinner className="border-t-white" /> Sending…</> : 'Send to all orgs'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {loading ? <Loader /> : (
        <Card className="overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Organisation</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Created</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Users</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Billing Exempt</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">AI Insights</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Pool Key</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => (
                <React.Fragment key={org.id}>
                  <tr className={cn('border-b border-slate-50 hover:bg-slate-50/50', expanded === org.id && 'bg-[hsl(138,26%,97%)]')}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <button
                          onClick={() => expand(org)}
                          className="bg-none border-none text-slate-400 p-0.5 flex transition-transform duration-150 cursor-pointer"
                          style={{ transform: expanded === org.id ? 'rotate(90deg)' : 'rotate(0)' }}
                        >
                          <svg width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                        <div className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: 'linear-gradient(135deg, hsl(138,26%,46%), hsl(138,26%,38%))' }}>
                          {org.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-slate-800">{org.name}</div>
                          <div className="text-[0.7rem] text-slate-500 font-mono">{org.slug}</div>
                        </div>
                        {org.id === 1 && <Badge variant="default">Primary</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{org.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 font-medium text-sm">{org.user_count}</td>
                    <td className="px-4 py-3">
                      {org.id !== 1 ? (
                        <button onClick={() => toggleActive(org)} className="cursor-pointer bg-transparent border-0 p-0">
                          <Badge variant={org.active ? 'green' : 'red'}>{org.active ? '● Active' : '● Suspended'}</Badge>
                        </button>
                      ) : <Badge variant="green">● Active</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => api(`/api/admin/orgs/${org.id}`, { method: 'PUT', body: JSON.stringify({ billing_exempt: org.billing_exempt ? 0 : 1 }) }).then(loadOrgs)}
                        className="cursor-pointer bg-transparent border-0 p-0">
                        <Badge variant={org.billing_exempt ? 'amber' : 'grey'}>{org.billing_exempt ? '✓ Exempt' : 'No'}</Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => api(`/api/admin/orgs/${org.id}`, { method: 'PUT', body: JSON.stringify({ insights_addon: org.insights_addon ? 0 : 1 }) }).then(loadOrgs)}
                        className="cursor-pointer bg-transparent border-0 p-0">
                        <Badge variant={org.insights_addon ? 'default' : 'grey'}>{org.insights_addon ? '✓ On' : 'Off'}</Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {org.pool_key_name
                        ? <Badge variant="green" title={org.pool_key_name}>✓ {org.pool_key_name.length > 16 ? org.pool_key_name.slice(0, 16) + '…' : org.pool_key_name}</Badge>
                        : <Badge variant="amber">None</Badge>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 justify-end flex-wrap">
                        <Btn variant="primary" size="xs" onClick={() => onViewDetail(org.id)}>Details</Btn>
                        <Btn size="xs" onClick={async () => {
                          const r = await api(`/api/admin/orgs/${org.id}/impersonate`, { method: 'POST' });
                          const d = await r.json();
                          if (d.url) window.open(d.url, '_blank');
                        }}>Open App ↗</Btn>
                        <Btn size="xs" onClick={() => setEditModal(org)}>Edit</Btn>
                        <Btn size="xs" onClick={() => refreshInvite(org)}>Invite</Btn>
                        {org.id !== 1 && (
                          <Btn variant="danger" size="xs" onClick={() => setDeleteConfirm(org)}>Delete</Btn>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expanded === org.id && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-[hsl(138,26%,97%)] border-b border-[hsl(138,26%,88%)]">
                        <div className="grid grid-cols-3 gap-6">
                          {/* Stats */}
                          <div>
                            <div className="text-[0.7rem] font-bold text-slate-500 uppercase tracking-wide mb-2.5">Data</div>
                            {orgStats[org.id] ? (
                              <div className="text-sm text-slate-500 flex flex-col gap-1.5">
                                <div>Orders: <strong className="text-slate-800">{orgStats[org.id].order_count?.toLocaleString()}</strong></div>
                                <div>Products: <strong className="text-slate-800">{orgStats[org.id].product_count?.toLocaleString()}</strong></div>
                                <div>Stores: <strong className="text-slate-800">{orgStats[org.id].stores?.length}</strong></div>
                                {orgStats[org.id].stores?.map(s => (
                                  <div key={s.id} className="text-xs text-slate-400 pl-3">
                                    • {s.shop_name || s.shop_url} {!s.active && <span className="text-red-500">(inactive)</span>}
                                  </div>
                                ))}
                              </div>
                            ) : <div className="text-sm text-slate-400">Loading...</div>}
                          </div>

                          {/* Gemini key */}
                          <div>
                            <div className="text-[0.7rem] font-bold text-slate-500 uppercase tracking-wide mb-2.5">Gemini API Key</div>
                            <GeminiKeyEditor org={org} onSave={loadOrgs} />
                          </div>

                          {/* Assign user */}
                          <div>
                            <div className="text-[0.7rem] font-bold text-slate-500 uppercase tracking-wide mb-2.5">Assign User</div>
                            {assignMsg && <div className={cn('text-xs mb-2', assignMsg.startsWith('✓') ? 'text-emerald-600' : 'text-red-500')}>{assignMsg}</div>}
                            <div className="flex flex-col gap-1.5">
                              <input
                                type="email"
                                value={assignEmail}
                                onChange={e => setAssignEmail(e.target.value)}
                                placeholder="user@example.com"
                                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
                              />
                              <div className="flex gap-1.5">
                                <select
                                  value={assignRole}
                                  onChange={e => setAssignRole(e.target.value)}
                                  className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
                                >
                                  <option value="admin">Admin</option>
                                  <option value="editor">Editor</option>
                                  <option value="viewer">Viewer</option>
                                </select>
                                <Btn variant="primary" size="sm" disabled={!assignEmail} onClick={() => assignUser(org.id)}>Assign</Btn>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Edit Modal */}
      {editModal && <EditOrgModal org={editModal} onClose={() => setEditModal(null)} onSave={() => { setEditModal(null); loadOrgs(); }} />}

      {/* Invite Modal */}
      {inviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setInviteModal(null)}>
          <div className="bg-white rounded-xl p-8 max-w-[480px] w-[90%] shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4">Invite Link</h2>
            <div className="bg-[hsl(138,26%,96%)] rounded-md px-3 py-3 text-sm text-[hsl(138,26%,40%)] break-all font-mono mb-4 border border-[hsl(138,26%,82%)]">
              {inviteModal.inviteUrl}
            </div>
            <p className="text-sm text-slate-500 mb-4">Expires 7 days from now. Share with client.</p>
            <div className="flex gap-2">
              <Btn variant="primary" className="flex-1 justify-center" onClick={() => { navigator.clipboard.writeText(inviteModal.inviteUrl); }}>Copy Link</Btn>
              <Btn className="flex-1 justify-center" onClick={() => setInviteModal(null)}>Done</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl p-8 max-w-[440px] w-[90%] shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-red-600 mb-1">Delete Organisation?</h2>
            <p className="text-slate-500 text-sm mb-5">
              This permanently deletes <strong className="text-slate-800">{deleteConfirm.name}</strong> and all its data. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Btn className="flex-1 justify-center" onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
              <Btn variant="danger" className="flex-1 justify-center" onClick={() => deleteOrg(deleteConfirm)}>Delete Permanently</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GeminiKeyEditor({ org, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  async function save() {
    await api(`/api/admin/orgs/${org.id}`, { method: 'PUT', body: JSON.stringify({ gemini_api_key: val }) });
    setEditing(false);
    onSave();
  }
  if (editing) return (
    <div className="flex flex-col gap-1.5">
      <input
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder="AIzaSy..."
        className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm font-mono shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
      />
      <div className="flex gap-1.5">
        <Btn variant="primary" size="sm" className="flex-1 justify-center" onClick={save}>Save</Btn>
        <Btn size="sm" onClick={() => setEditing(false)}>Cancel</Btn>
      </div>
    </div>
  );
  return (
    <button
      className={cn('bg-transparent border-0 p-0 text-sm cursor-pointer', org.gemini_api_key ? 'text-emerald-600' : 'text-slate-500')}
      onClick={() => { setEditing(true); setVal(org.gemini_api_key || ''); }}
    >
      {org.gemini_api_key ? '✓ Key set — click to change' : '+ Set Gemini API key'}
    </button>
  );
}

function EditOrgModal({ org, onClose, onSave }) {
  const [name, setName] = useState(org.name);
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    await api(`/api/admin/orgs/${org.id}`, { method: 'PUT', body: JSON.stringify({ name }) });
    setSaving(false);
    onSave();
  }
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl p-8 max-w-[420px] w-[90%] shadow-xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">Edit Organisation</h2>
        <label className="block text-xs text-slate-500 font-medium mb-1.5">Name</label>
        <input
          className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)] mb-5"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <div className="flex gap-2">
          <Btn className="flex-1 justify-center" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" className="flex-1 justify-center" onClick={save} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Stripe Page ───────────────────────────────────────────────────────────────
function StripePage() {
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [subsFilter, setSubsFilter] = useState('active');

  useEffect(() => {
    api('/api/admin/stripe/overview').then(r => r.json()).then(d => { setOverview(d); setLoading(false); });
  }, []);

  useEffect(() => {
    if (tab === 'subscriptions') {
      setSubs([]);
      api(`/api/admin/stripe/subscriptions?status=${subsFilter}`).then(r => r.json()).then(d => setSubs(d.subscriptions || []));
    }
  }, [tab, subsFilter]);

  if (loading) return <Loader />;

  if (overview?.error) return (
    <div>
      <div className="text-2xl font-bold tracking-tight mb-4">Stripe</div>
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">{overview.error}</div>
    </div>
  );

  const cur = overview?.currency || 'gbp';

  return (
    <div>
      <div className="text-2xl font-bold tracking-tight">Stripe</div>
      <div className="text-sm text-slate-500 mb-6">Live billing data from your Stripe account</div>

      {/* KPI Row */}
      <div className="grid gap-3.5 mb-7" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))' }}>
        <StatCard label="MRR" value={fmt(overview.mrr, cur)} sub="Monthly recurring rev" />
        <StatCard label="ARR" value={fmt(overview.arr, cur)} sub="Annualised run rate" />
        <StatCard label="Active Subs" value={overview.active_subscriptions} sub="Live subscriptions" />
        <StatCard label="30d Revenue" value={fmt(overview.revenue_30d, cur)} sub="Last 30 days" />
        <StatCard label="Failed Charges" value={overview.failed_charges_30d} sub="Last 30 days" />
        <StatCard label="Available Balance" value={fmt(overview.available_balance, cur)} sub="Stripe balance" />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {[['overview', 'Recent Charges'], ['subscriptions', 'Subscriptions']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent',
            tab === k ? 'border-[hsl(138,26%,46%)] text-[hsl(138,26%,46%)] font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'
          )}>{l}</button>
        ))}
      </div>

      {tab === 'overview' && <StripeChargesTable />}
      {tab === 'subscriptions' && (
        <div>
          <div className="mb-3 flex gap-2 flex-wrap">
            {['active', 'past_due', 'canceled', 'trialing'].map(s => (
              <button key={s} onClick={() => setSubsFilter(s)} className="cursor-pointer bg-transparent border-0 p-0">
                <Badge variant={subsFilter === s ? 'default' : 'grey'} className={subsFilter === s ? 'cursor-pointer' : 'cursor-pointer'}>{s}</Badge>
              </button>
            ))}
          </div>
          {subs.length === 0 ? (
            <div className="text-slate-400 py-8 text-center text-sm">No subscriptions found</div>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Plan</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Renews</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(sub => (
                    <tr key={sub.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm">{sub.customer_name || '—'}</div>
                        <div className="text-xs text-slate-500">{sub.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {sub.items.map((it, i) => (
                          <div key={i}>{it.description || '—'} {it.amount ? `· ${fmt(it.amount, cur)}/${it.interval}` : ''}</div>
                        ))}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={sub.status === 'active' ? 'green' : sub.status === 'past_due' ? 'red' : sub.status === 'trialing' ? 'amber' : 'grey'}>
                          {sub.status}{sub.cancel_at_period_end ? ' · cancels' : ''}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(sub.current_period_end)}</td>
                      <td className="px-4 py-3 font-mono text-[0.72rem] text-slate-400">{sub.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function StripeChargesTable() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api('/api/admin/stripe/charges').then(r => r.json()).then(d => { setCharges(d.charges || []); setLoading(false); });
  }, []);

  if (loading) return <Loader />;
  if (!charges.length) return <div className="text-slate-400 py-8 text-center text-sm">No charges found</div>;

  return (
    <Card className="overflow-hidden">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Amount</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Description</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Date</th>
          </tr>
        </thead>
        <tbody>
          {charges.map(c => (
            <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
              <td className="px-4 py-3">
                <div className="font-medium text-sm">{c.customer_name || '—'}</div>
                <div className="text-xs text-slate-500">{c.customer_email}</div>
              </td>
              <td className="px-4 py-3 font-semibold text-sm">{fmt(c.amount, c.currency)}</td>
              <td className="px-4 py-3">
                <Badge variant={c.status === 'succeeded' ? 'green' : c.status === 'failed' ? 'red' : 'grey'}>{c.status}</Badge>
                {c.failure_message && <div className="text-[0.7rem] text-red-500 mt-0.5">{c.failure_message}</div>}
              </td>
              <td className="px-4 py-3 text-slate-500 text-xs">{c.description || '—'}</td>
              <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(c.created)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ── Provision Page ────────────────────────────────────────────────────────────
function ProvisionPage() {
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  async function create() {
    if (!orgName.trim()) return;
    setLoading(true);
    setError('');
    try {
      const r = await api('/api/admin/orgs', { method: 'POST', body: JSON.stringify({ orgName: orgName.trim() }) });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed'); return; }
      setResult(d);
      setOrgName('');
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  function copy() {
    navigator.clipboard.writeText(result.inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="max-w-[540px]">
      <div className="text-2xl font-bold tracking-tight">Provision New Client</div>
      <div className="text-sm text-slate-500 mb-6">Create an organisation and generate an onboarding invite link</div>

      {!result ? (
        <Card className="p-7">
          <div className="mb-5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Organisation Name</label>
            <input
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-base shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
              type="text"
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              placeholder="Acme Co."
              onKeyDown={e => e.key === 'Enter' && create()}
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2.5 text-red-600 text-sm mb-4">{error}</div>
          )}

          <Btn variant="primary" className="w-full justify-center h-10 text-base" disabled={!orgName.trim() || loading} onClick={create}>
            {loading ? <><Spinner className="border-t-white" /> Creating...</> : 'Create Organisation & Generate Invite'}
          </Btn>

          <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-[0.72rem] font-bold text-slate-500 uppercase tracking-wide mb-2">What happens next</div>
            <div className="text-sm text-slate-500 flex flex-col gap-1.5">
              <div>1. An organisation is created in the database</div>
              <div>2. A 7-day invite link is generated</div>
              <div>3. Client visits the link and completes onboarding (sets up account + connects Shopify)</div>
              <div>4. Their org schema is automatically provisioned</div>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-7">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">✓</div>
            <div className="text-lg font-bold text-emerald-600 mb-1">Organisation Created</div>
            <div className="text-sm text-slate-500">Share the invite link with your client</div>
          </div>

          <div className="mb-4">
            <div className="text-[0.72rem] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Invite Link</div>
            <div className="bg-[hsl(138,26%,96%)] rounded-md px-4 py-3 text-sm text-[hsl(138,26%,40%)] break-all font-mono border border-[hsl(138,26%,82%)]">
              {result.inviteUrl}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-[0.65rem] text-slate-400 uppercase mb-1">Org ID</div>
              <div className="font-mono font-semibold text-slate-800">{result.orgId}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-[0.65rem] text-slate-400 uppercase mb-1">Expires</div>
              <div className="text-sm text-slate-700">{fmtDate(result.expiresAt)}</div>
            </div>
          </div>

          <div className="flex gap-2">
            <Btn variant="primary" className="flex-[2] justify-center" onClick={copy}>
              {copied ? '✓ Copied!' : 'Copy Invite Link'}
            </Btn>
            <Btn className="flex-1 justify-center" onClick={() => setResult(null)}>New Org</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Shopify Tab ───────────────────────────────────────────────────────────────
function ShopifyTab({ orgId, shopify }) {
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProg, setSyncProg] = useState(null);
  const [syncDays, setSyncDays] = useState(730);
  const [syncLog, setSyncLog] = useState([]);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [productSyncMsg, setProductSyncMsg] = useState('');
  const pollRef = useRef(null);

  // Event timeline state
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsFilter, setEventsFilter] = useState('');
  const [eventsLoading, setEventsLoading] = useState(false);

  function fmtGBP(n) { return '£' + Number(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  function loadSyncLog() {
    api('/api/admin/sync-log').then(r => r.json()).then(d => { if (Array.isArray(d)) setSyncLog(d); }).catch(() => {});
  }

  function loadEvents(page = 1, type = eventsFilter) {
    setEventsLoading(true);
    const qs = `page=${page}&limit=10${type ? `&type=${type}` : ''}`;
    api(`/api/admin/orgs/${orgId}/events?${qs}`).then(r => r.json()).then(d => {
      setEvents(d.events || []);
      setEventsTotal(d.total || 0);
      setEventsPage(d.page || 1);
      setEventsTotalPages(d.totalPages || 1);
    }).catch(() => {}).finally(() => setEventsLoading(false));
  }

  useEffect(() => {
    api(`/api/admin/orgs/${orgId}/sync-status`).then(r => r.json()).then(d => setSyncStatus(d)).catch(() => {});
    // Check if a sync is already running
    api(`/api/admin/orgs/${orgId}/sync-progress`).then(r => r.json()).then(p => {
      if (p?.status === 'running') { setSyncing(true); setSyncProg(p); startPolling(); }
      else if (p?.status === 'complete' || p?.status === 'error') setSyncProg(p);
    }).catch(() => {});
    loadSyncLog();
    loadEvents(1, '');
  }, [orgId]);

  function startPolling() {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      api(`/api/admin/orgs/${orgId}/sync-progress`).then(x => x.json()).then(p => {
        setSyncProg(p);
        if (p?.status !== 'running') {
          setSyncing(false);
          clearInterval(pollRef.current);
          loadSyncLog();
          loadEvents(1, eventsFilter);
          api(`/api/admin/orgs/${orgId}/sync-status`).then(r => r.json()).then(d => setSyncStatus(d)).catch(() => {});
        }
      });
    }, 3000);
    setTimeout(() => { clearInterval(pollRef.current); setSyncing(false); }, 300000);
  }

  async function triggerSync() {
    setSyncing(true); setSyncProg({ status: 'running', phase: 'Starting…', orders: 0, products: 0 });
    const r = await api(`/api/admin/orgs/${orgId}/sync`, { method: 'POST', body: JSON.stringify({ days: syncDays }) });
    const d = await r.json();
    if (!r.ok) { setSyncProg({ status: 'error', error: d.error || 'Sync failed' }); setSyncing(false); return; }
    startPolling();
  }

  useEffect(() => () => clearInterval(pollRef.current), []);

  function fmtSync(ts) {
    if (!ts) return 'Never';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }

  function fmtDuration(start, end) {
    if (!start || !end) return '—';
    const ms = new Date(end) - new Date(start);
    const s = Math.floor(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  const rangeOpts = [
    { label: '30 days', value: 30 }, { label: '90 days', value: 90 },
    { label: '6 months', value: 180 }, { label: '1 year', value: 365 },
    { label: '2 years', value: 730 }, { label: '3 years', value: 1095 },
  ];

  const o = syncProg ? (syncProg.orders ?? syncProg.orders_synced ?? 0) : 0;
  const pr = syncProg ? (syncProg.products ?? syncProg.products_synced ?? 0) : 0;

  return (
    <div>
      <div className="grid gap-3.5 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
        <StatCard label="Total Revenue" value={fmtGBP(shopify.total_revenue)} sub="All time" />
        <StatCard label="30d Revenue" value={fmtGBP(shopify.revenue_30d)} sub="Last 30 days" />
        <StatCard label="Orders" value={fmtN(shopify.order_count)} sub="Total" />
        <StatCard label="30d Orders" value={fmtN(shopify.orders_30d)} sub="Last 30 days" />
        <StatCard label="Avg Order Value" value={fmtGBP(shopify.avg_order_value)} sub="All time" />
        <StatCard label="Customers" value={fmtN(shopify.unique_customers)} sub="Unique customers" />
      </div>

      {/* Sync controls */}
      <Card className="px-5 py-4 mb-4">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1.5">Data Sync</div>
            {syncStatus ? (
              <div className="flex gap-6 text-xs text-slate-500">
                <span>Orders: <strong className="text-slate-700">{syncStatus.latest?.orders?.records_synced ?? '—'}</strong> · Last: {fmtSync(syncStatus.latest?.orders?.last_sync)}</span>
                <span>Products: <strong className="text-slate-700">{syncStatus.latest?.products?.records_synced ?? '—'}</strong> · Last: {fmtSync(syncStatus.latest?.products?.last_sync)}</span>
              </div>
            ) : <div className="text-xs text-slate-300">Loading sync status…</div>}
          </div>
          <select value={syncDays} onChange={e => setSyncDays(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white text-slate-600">
            {rangeOpts.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Btn variant="primary" size="sm" onClick={triggerSync} disabled={syncing}>
            {syncing ? <><Spinner className="border-t-white" /> Syncing…</> : '↻ Full Sync'}
          </Btn>
          <Btn size="sm" onClick={async () => {
            setSyncingProducts(true); setProductSyncMsg(''); setSyncProg(null);
            const r = await api(`/api/admin/orgs/${orgId}/sync-products`, { method: 'POST', body: JSON.stringify({ full: true }) });
            const d = await r.json();
            if (!r.ok) { setSyncingProducts(false); setProductSyncMsg(d.error || 'Failed'); return; }
            setProductSyncMsg('running');
            // Poll sync-status every 3s until products last_sync updates
            const startedAt = syncStatus?.latest?.products?.last_sync || null;
            const prodPoll = setInterval(() => {
              api(`/api/admin/orgs/${orgId}/sync-status`).then(x => x.json()).then(s => {
                const newSync = s?.latest?.products?.last_sync;
                if (newSync && newSync !== startedAt) {
                  clearInterval(prodPoll);
                  setSyncStatus(s);
                  setSyncingProducts(false);
                  setProductSyncMsg('complete');
                  loadSyncLog();
                  loadEvents(1, eventsFilter);
                  setTimeout(() => setProductSyncMsg(''), 8000);
                }
              }).catch(() => {});
            }, 3000);
            // Timeout after 5 minutes
            setTimeout(() => { clearInterval(prodPoll); setSyncingProducts(false); if (productSyncMsg === 'running') setProductSyncMsg(''); }, 300000);
          }} disabled={syncingProducts || syncing}>
            {syncingProducts ? <><Spinner /> Products…</> : '↻ Products Only'}
          </Btn>
        </div>

        {productSyncMsg === 'running' && (
          <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700">
            <strong>Syncing products:</strong> Fetching all products from Shopify…
          </div>
        )}
        {productSyncMsg === 'complete' && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
            <strong>Complete:</strong> Product sync finished — {syncStatus?.latest?.products?.records_synced ?? '—'} products synced
          </div>
        )}
        {productSyncMsg && productSyncMsg !== 'running' && productSyncMsg !== 'complete' && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
            <strong>Error:</strong> {productSyncMsg}
          </div>
        )}

        {/* Live progress */}
        {syncProg && syncProg.status === 'running' && (
          <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700">
            <strong>Syncing:</strong> {syncProg.phase || 'Starting…'} — {o} orders, {pr} products
          </div>
        )}
        {syncProg && syncProg.status === 'complete' && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
            <strong>Complete:</strong> Synced {o} orders, {pr} products
          </div>
        )}
        {syncProg && syncProg.status === 'error' && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
            <strong>Error:</strong> {syncProg.error || 'Unknown error'} — {o} orders, {pr} products synced before failure
          </div>
        )}
      </Card>

      {/* Sync history log */}
      {syncLog.filter(l => l.org_id === orgId).length > 0 && (
        <Card className="overflow-hidden mb-4">
          <div className="px-5 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">
            Sync History
          </div>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/50 text-left text-slate-500">
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Range</th>
                <th className="px-4 py-2 font-medium">Orders</th>
                <th className="px-4 py-2 font-medium">Products</th>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {syncLog.filter(l => l.org_id === orgId).slice(0, 10).map(l => (
                <tr key={l.id} className="border-t border-slate-50">
                  <td className="px-4 py-2">
                    <span className={cn('inline-block px-1.5 py-0.5 rounded text-[0.65rem] font-medium',
                      l.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                      l.status === 'running' ? 'bg-blue-100 text-blue-700' :
                      'bg-red-100 text-red-600'
                    )}>{l.status}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{l.range_days ? `${l.range_days}d` : '—'}</td>
                  <td className="px-4 py-2 text-slate-700 font-medium">{l.orders_synced ?? 0}</td>
                  <td className="px-4 py-2 text-slate-700 font-medium">{l.products_synced ?? 0}</td>
                  <td className="px-4 py-2 text-slate-500">{fmtSync(l.started_at)}</td>
                  <td className="px-4 py-2 text-slate-500">{fmtDuration(l.started_at, l.completed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="mt-3 text-xs text-slate-400">
        {shopify.first_order && <span>First order: {shopify.first_order.slice(0, 10)} · </span>}
        {shopify.last_order && <span>Last order: {shopify.last_order.slice(0, 10)}</span>}
      </div>

      {/* Organisation Event Timeline */}
      <Card className="overflow-hidden mt-4">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Event Timeline</div>
          <div className="flex items-center gap-2">
            <select value={eventsFilter} onChange={e => { setEventsFilter(e.target.value); loadEvents(1, e.target.value); }}
              className="text-[0.65rem] border border-slate-200 rounded px-2 py-1 bg-white text-slate-600">
              <option value="">All events</option>
              <option value="org_created">Org created</option>
              <option value="trial_started">Trial started</option>
              <option value="subscription_activated">Subscription activated</option>
              <option value="subscription_changed">Subscription changed</option>
              <option value="subscription_canceled">Subscription canceled</option>
              <option value="credits_subscribed">Credits subscribed</option>
              <option value="shopify_connected">Shopify connected</option>
              <option value="sync_complete">Sync complete</option>
              <option value="sync_error">Sync error</option>
              <option value="backfill_started">Backfill started</option>
            </select>
            <span className="text-[0.6rem] text-slate-400">{eventsTotal} event{eventsTotal !== 1 ? 's' : ''}</span>
          </div>
        </div>
        {eventsLoading && events.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-xs"><Spinner /> Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-xs">No events recorded yet</div>
        ) : (
          <>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/50 text-left text-slate-500">
                  <th className="px-4 py-2 font-medium w-8"></th>
                  <th className="px-4 py-2 font-medium">Event</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">By</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => {
                  const icons = {
                    org_created: ['🏢', 'blue'], trial_started: ['⏱', 'amber'], subscription_activated: ['✓', 'green'],
                    subscription_changed: ['↕', 'blue'], subscription_canceled: ['✕', 'red'], credits_subscribed: ['✦', 'green'],
                    shopify_connected: ['🛍', 'green'], sync_complete: ['↻', 'green'], sync_error: ['⚠', 'red'],
                    backfill_started: ['📦', 'blue'],
                  };
                  const [icon, variant] = icons[ev.event_type] || ['•', 'grey'];
                  return (
                    <tr key={ev.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-2 text-center">{icon}</td>
                      <td className="px-4 py-2">
                        <Badge variant={variant} className="text-[0.6rem]">{ev.event_type.replace(/_/g, ' ')}</Badge>
                      </td>
                      <td className="px-4 py-2 text-slate-600 max-w-[280px] truncate">{ev.description}</td>
                      <td className="px-4 py-2 text-slate-400">{ev.actor || '—'}</td>
                      <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {eventsTotalPages > 1 && (
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                <Btn size="xs" variant="ghost" disabled={eventsPage <= 1} onClick={() => { setEventsPage(eventsPage - 1); loadEvents(eventsPage - 1); }}>← Prev</Btn>
                <span className="text-[0.65rem] text-slate-400">Page {eventsPage} of {eventsTotalPages}</span>
                <Btn size="xs" variant="ghost" disabled={eventsPage >= eventsTotalPages} onClick={() => { setEventsPage(eventsPage + 1); loadEvents(eventsPage + 1); }}>Next →</Btn>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ── Org Detail Page ───────────────────────────────────────────────────────────
function OrgDetailPage({ orgId, initialTab, onBack, onTabChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab || 'overview');

  useEffect(() => {
    api(`/api/admin/orgs/${orgId}/detail`).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [orgId]);

  if (loading) return <Loader />;
  if (!data) return <div className="p-8 text-red-500">Failed to load organisation details.</div>;

  const { org, ai, users, shopify, mmm, poolKey } = data;
  const TABS = ['overview', 'shopify', 'ai usage', 'users', 'mmm'];
  const GREEN = 'hsl(138, 26%, 46%)';

  function fmtGBP(n) { return '£' + Number(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  const statusColor = { active: '#10b981', trialing: '#f59e0b', past_due: '#ef4444', canceled: '#ef4444' };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Btn size="sm" onClick={onBack}>← Back</Btn>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-base font-extrabold text-white shrink-0" style={{ background: 'linear-gradient(135deg, hsl(138,26%,46%), hsl(138,26%,38%))' }}>
          {org.name.charAt(0)}
        </div>
        <div>
          <div className="text-lg font-bold">{org.name}</div>
          <div className="text-xs text-slate-400 font-mono">{org.slug} · ID {org.id}</div>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: (statusColor[org.subscription_status] || '#888') + '20', color: statusColor[org.subscription_status] || '#888' }}>
            {org.subscription_status || 'No subscription'}
          </span>
          {org.billing_exempt && <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold border border-amber-200">Billing exempt</span>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6">
        {TABS.map(t => (
          <button key={t} onClick={() => { setTab(t); onTabChange?.(t); }} className={cn(
            'px-4 py-2 text-sm border-b-2 -mb-px transition-colors cursor-pointer bg-transparent capitalize',
            tab === t ? 'border-[hsl(138,26%,46%)] text-[hsl(138,26%,46%)] font-semibold' : 'border-transparent text-slate-500 font-normal hover:text-slate-800'
          )}>{t}</button>
        ))}
      </div>

      {/* ── Overview Tab ── */}
      {tab === 'overview' && (
        <div>
          <div className="grid gap-3.5 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
            <StatCard label="Total Revenue" value={fmtGBP(shopify.total_revenue)} sub="All time" />
            <StatCard label="30d Revenue" value={fmtGBP(shopify.revenue_30d)} sub={`${fmtN(shopify.orders_30d)} orders`} />
            <StatCard label="Total Orders" value={fmtN(shopify.order_count)} sub={`AOV ${fmtGBP(shopify.avg_order_value)}`} />
            <StatCard label="Customers" value={fmtN(shopify.unique_customers)} sub="Unique" />
            <StatCard label="AI Calls" value={fmtN(ai.totalCalls)} sub={`${fmtN(ai.totalTokens)} tokens · est. ${fmtCost(geminiCostFromTotal(ai.totalTokens))}`} />
            <StatCard label="MMM Runs" value={fmtN(mmm.slots_used)} sub={`of ${mmm.slots_total} slots`} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Billing */}
            <Card className="p-5">
              <div className="text-[0.7rem] font-bold text-slate-400 uppercase tracking-wide mb-3.5">Billing</div>
              <div className="flex flex-col gap-2 text-sm">
                {[
                  ['Plan', org.subscription_plan || '—'],
                  ['Status', org.subscription_status || '—', statusColor[org.subscription_status] || '#888'],
                  ['Trial ends', org.trial_ends_at ? org.trial_ends_at.slice(0, 10) : '—'],
                  ['Token balance', fmtN(org.token_balance)],
                  ['Monthly AI credits', fmtN(org.credits_monthly)],
                  ['Stripe customer ID', org.stripe_customer_id || '—'],
                  ['Add-ons', [org.insights_addon && 'AI Insights', org.billing_exempt && 'Billing exempt'].filter(Boolean).join(', ') || 'None'],
                ].map(([label, value, color]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-slate-500">{label}</span>
                    <span className="font-semibold" style={color ? { color } : {}}>{value}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Org info */}
            <Card className="p-5">
              <div className="text-[0.7rem] font-bold text-slate-400 uppercase tracking-wide mb-3.5">Organisation</div>
              <div className="flex flex-col gap-2 text-sm">
                {[
                  ['Created', fmtDate(org.created_at)],
                  ['Users', fmtN(users.length)],
                  ['Stores connected', fmtN(shopify.stores.length)],
                  ['MMM runs', `${mmm.slots_used} / ${mmm.slots_total} slots`],
                  ['Gemini key', org.gemini_api_key ? '✓ Set (own)' : 'Not set', org.gemini_api_key ? '#10b981' : '#999'],
                  ['Pool key', poolKey ? `✓ ${poolKey.name}` : 'None assigned', poolKey ? '#10b981' : '#f59e0b'],
                  ['Active', org.active ? 'Yes' : 'Suspended', org.active ? '#10b981' : '#ef4444'],
                ].map(([label, value, color]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-slate-500">{label}</span>
                    <span className="font-semibold" style={color ? { color } : {}}>{value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Connected Stores */}
          <Card className="overflow-hidden mt-4">
            <div className="px-5 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">
              Connected Stores
            </div>
            {shopify.stores.length === 0 ? (
              <div className="p-6 text-slate-400 text-sm text-center">No stores connected</div>
            ) : (
              <table className="w-full border-collapse">
                <tbody>
                  {shopify.stores.map(s => (
                    <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-sm">{s.shop_name || s.shop_url}</div>
                        <div className="text-xs text-slate-400">{s.shop_url}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{s.connected_at ? s.connected_at.slice(0, 10) : '—'}</td>
                      <td className="px-4 py-3">
                        <Badge variant={s.active ? 'green' : 'red'}>{s.active ? 'Active' : 'Inactive'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}

      {/* ── Shopify Tab ── */}
      {tab === 'shopify' && <ShopifyTab orgId={orgId} shopify={shopify} />}

      {/* ── AI Usage Tab ── */}
      {tab === 'ai usage' && (
        <div>
          {(() => {
            const totalCost = ai.byEndpoint.length > 0
              ? ai.byEndpoint.reduce((acc, e) => acc + geminiCost(e.prompt_tokens, e.output_tokens), 0)
              : geminiCostFromTotal(ai.totalTokens);
            return (
              <div className="grid gap-3.5 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                <StatCard label="Total API Calls" value={fmtN(ai.totalCalls)} sub="All time" />
                <StatCard label="Total Tokens" value={fmtN(ai.totalTokens)} sub="Prompt + output" />
                <StatCard label="Est. AI Cost" value={fmtCost(totalCost)} sub="Gemini 2.0 Flash rates" />
              </div>
            );
          })()}

          <div className="text-xs text-slate-400 mb-4 flex items-center gap-1.5">
            <svg width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            Estimated at Gemini 2.0 Flash rates: $0.075/1M input tokens · $0.30/1M output tokens. Actual cost may vary by model used.
          </div>

          {ai.byEndpoint.length > 0 && (
            <Card className="overflow-hidden mb-4">
              <div className="px-5 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">By Endpoint</div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Endpoint</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Calls</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Input tokens</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Output tokens</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Total tokens</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Est. cost</th>
                  </tr>
                </thead>
                <tbody>
                  {ai.byEndpoint.map(e => (
                    <tr key={e.endpoint} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3"><code className="text-sm">{e.endpoint}</code></td>
                      <td className="px-4 py-3 text-sm">{fmtN(e.call_count)}</td>
                      <td className="px-4 py-3 text-sm">{fmtN(e.prompt_tokens)}</td>
                      <td className="px-4 py-3 text-sm">{fmtN(e.output_tokens)}</td>
                      <td className="px-4 py-3 text-sm font-semibold">{fmtN(e.total_tokens)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-purple-600">{fmtCost(geminiCost(e.prompt_tokens, e.output_tokens))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {ai.daily.length > 0 && (
            <Card className="overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">Daily Usage (Last 14 Days)</div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Calls</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Tokens</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Est. cost</th>
                  </tr>
                </thead>
                <tbody>
                  {ai.daily.map(d => (
                    <tr key={d.day} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-sm">{d.day}</td>
                      <td className="px-4 py-3 text-sm">{fmtN(d.calls)}</td>
                      <td className="px-4 py-3 text-sm">{fmtN(d.tokens)}</td>
                      <td className="px-4 py-3 text-sm text-purple-600">{fmtCost(geminiCostFromTotal(d.tokens))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {ai.totalCalls === 0 && (
            <div className="py-12 text-center text-slate-400 text-sm">
              No AI usage recorded for this organisation yet.
            </div>
          )}
        </div>
      )}

      {/* ── Users Tab ── */}
      {tab === 'users' && (
        <Card className="overflow-hidden">
          {users.length === 0 ? (
            <div className="p-6 text-slate-400 text-sm text-center">No users</div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-semibold text-sm">{u.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={u.role === 'admin' ? 'default' : 'grey'}>{u.role}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{u.last_login ? timeAgo(u.last_login) : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* ── MMM Tab ── */}
      {tab === 'mmm' && (
        <div>
          <div className="mb-4 text-sm text-slate-500">
            Slots: <strong className="text-slate-800">{mmm.slots_used} used</strong> of {mmm.slots_total} ({org.mmm_run_slots || 0} add-on packs)
          </div>
          <Card className="overflow-hidden">
            {mmm.runs.length === 0 ? (
              <div className="p-6 text-slate-400 text-sm text-center">No MMM runs</div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Framework</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Label</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Fit</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Duration</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {mmm.runs.map(r => {
                    const fwBg = { ols: '#f3f4f6', pymc: '#ede9fe', meridian: '#dbeafe', robyn: '#ffedd5' }[r.framework] || '#f3f4f6';
                    const fwColor = { ols: '#374151', pymc: '#7c3aed', meridian: '#1d4ed8', robyn: '#c2410c' }[r.framework] || '#374151';
                    const stColor = { complete: '#10b981', running: '#3b82f6', failed: '#ef4444', pending: '#f59e0b' }[r.status] || '#888';
                    return (
                      <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: fwBg, color: fwColor }}>{r.framework}</span>
                        </td>
                        <td className="px-4 py-3 text-sm">{r.label || '—'}</td>
                        <td className="px-4 py-3 text-xs font-medium" style={{ color: stColor }}>● {r.status}</td>
                        <td className="px-4 py-3 text-sm">{r.model_fit ? r.model_fit.toFixed(3) : '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{r.duration_s ? `${r.duration_s}s` : '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{r.created_at ? r.created_at.slice(0, 10) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Login Page ────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function login(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Login failed'); return; }
      localStorage.setItem('admin_token', d.token);
      onLogin(d);
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  return (
    <div className="flex items-center justify-center min-h-screen w-full bg-slate-50">
      <div className="w-full max-w-[380px]">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl font-extrabold text-white mx-auto mb-4" style={{ background: 'linear-gradient(135deg, hsl(138,26%,46%), hsl(138,26%,38%))' }}>N</div>
          <div className="text-2xl font-bold text-slate-800">Nuso Control Room</div>
          <div className="text-slate-400 text-sm mt-1">Superadmin access only</div>
        </div>

        <Card className="p-7">
          <form onSubmit={login}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2.5 text-red-600 text-sm mb-4 text-center">{error}</div>
            )}
            <div className="mb-3.5">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
                placeholder="you@nuso.co.uk"
                autoFocus
                required
              />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
                required
              />
            </div>
            <Btn type="submit" variant="primary" className="w-full justify-center h-10 text-base" disabled={loading}>
              {loading ? <><Spinner className="border-t-white" /> Signing in...</> : 'Sign in'}
            </Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}

// ── Support Page ──────────────────────────────────────────────────────────────
function SupportPage() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [showDetails, setShowDetails] = useState(false);
  const [orgDetails, setOrgDetails] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const pollRef = useRef(null);
  const chatRef = useRef(null);

  const loadConvs = useCallback(() => {
    api(`/api/admin/support?unread=${unreadOnly ? 1 : 0}&status=${statusFilter}`)
      .then(r => r.json()).then(d => { setConvs(d.conversations || []); setLoading(false); });
  }, [unreadOnly, statusFilter]);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    if (!selected) { setShowDetails(false); return; }
    setOrgDetails(null);
    api(`/api/admin/support/${selected.id}/messages`)
      .then(r => r.json()).then(d => {
        setMessages(d.messages || []);
        setConvs(c => c.map(x => x.id === selected.id ? { ...x, unread_admin: 0 } : x));
        setTimeout(() => { chatRef.current?.scrollTo(0, chatRef.current.scrollHeight); }, 50);
      });
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      api(`/api/admin/support/${selected.id}/messages`)
        .then(r => r.json()).then(d => setMessages(d.messages || []));
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [selected]);

  useEffect(() => {
    chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
  }, [messages]);

  // Load org detail when panel opens
  useEffect(() => {
    if (!showDetails || !selected?.org_id || orgDetails?.org?.id === selected.org_id) return;
    setOrgLoading(true);
    api(`/api/admin/orgs/${selected.org_id}`)
      .then(r => r.json())
      .then(d => { setOrgDetails(d); setOrgLoading(false); })
      .catch(() => setOrgLoading(false));
  }, [showDetails, selected]);

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim() || !selected) return;
    setSending(true);
    await api(`/api/admin/support/${selected.id}/reply`, { method: 'POST', body: JSON.stringify({ content: reply.trim() }) });
    setReply('');
    setSending(false);
    api(`/api/admin/support/${selected.id}/messages`).then(r => r.json()).then(d => setMessages(d.messages || []));
  }

  async function toggleStatus() {
    const newStatus = selected.status === 'open' ? 'closed' : 'open';
    await api(`/api/admin/support/${selected.id}/status`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
    setSelected(s => ({ ...s, status: newStatus }));
    setConvs(c => c.map(x => x.id === selected.id ? { ...x, status: newStatus } : x));
  }

  async function triggerSync() {
    if (!selected?.org_id) return;
    if (!confirm('Trigger a full data resync for this org?')) return;
    const r = await api(`/api/admin/orgs/${selected.org_id}/sync`, { method: 'POST', body: JSON.stringify({ full: true }) });
    alert(r.ok ? 'Sync triggered — data will refresh in a few minutes.' : 'Sync failed — check server logs.');
  }

  // ── Detail Panel ─────────────────────────────────────────────────────────
  function DetailPanel() {
    if (!selected) return null;
    const org = orgDetails?.org;
    const users = orgDetails?.users || [];
    const stores = orgDetails?.stores || [];
    const stripe = orgDetails?.stripe;

    const initials = (selected.user_name || selected.user_email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

    return (
      <div className="w-72 shrink-0 border-l border-slate-200 bg-white flex flex-col overflow-hidden">
        {/* Panel header */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Details</span>
          <button onClick={() => setShowDetails(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded transition-colors">
            <svg width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {orgLoading && <div className="p-4 text-sm text-slate-400"><Spinner /></div>}
          {!orgLoading && (
            <>
              {/* User */}
              <div className="px-4 pt-4 pb-3 border-b border-slate-100">
                <div className="text-[0.68rem] font-bold uppercase tracking-widest text-slate-400 mb-2.5">User</div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                    style={{ background: 'linear-gradient(135deg, hsl(138,26%,46%), hsl(138,26%,34%))' }}>
                    {initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{selected.user_name || '—'}</div>
                    <div className="text-xs text-slate-400">{selected.user_email}</div>
                  </div>
                </div>
                {users.length > 0 && (() => {
                  const u = users.find(x => x.email === selected.user_email) || users[0];
                  return (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Role</span>
                        <span className="text-slate-700 font-medium capitalize">{u?.role || '—'}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Last login</span>
                        <span className="text-slate-700">{u?.last_login ? fmtDate(u.last_login) : '—'}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Organisation */}
              <div className="px-4 pt-3.5 pb-3 border-b border-slate-100">
                <div className="text-[0.68rem] font-bold uppercase tracking-widest text-slate-400 mb-2.5">Organisation</div>
                <div className="text-sm font-semibold text-slate-900 mb-2">{org?.name || selected.org_name || `Org ${selected.org_id}`}</div>
                <div className="space-y-1 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Users</span>
                    <span className="text-slate-700 font-medium">{users.length || '—'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Stores connected</span>
                    <span className="text-slate-700 font-medium">{stores.length || 0}</span>
                  </div>
                  {stores.map(s => (
                    <div key={s.id} className="flex justify-between text-xs pl-2">
                      <span className="text-slate-400 truncate max-w-[130px]">{s.store_domain}</span>
                      <Badge variant={s.sync_status === 'complete' ? 'green' : 'amber'} className="text-[0.6rem]">{s.sync_status || 'pending'}</Badge>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Created</span>
                    <span className="text-slate-700">{org?.created_at ? fmtDate(org.created_at) : '—'}</span>
                  </div>
                </div>
                <Btn size="sm" variant="primary" className="w-full" onClick={triggerSync}>
                  Trigger Sync
                </Btn>
              </div>

              {/* Billing */}
              <div className="px-4 pt-3.5 pb-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-widest text-slate-400 mb-2.5">Billing</div>
                {stripe?.error ? (
                  <div className="text-xs text-slate-400">Stripe not linked</div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Status</span>
                      <Badge variant={stripe?.subscription_status === 'active' ? 'green' : 'amber'} className="text-[0.6rem]">
                        {stripe?.subscription_status || 'No subscription'}
                      </Badge>
                    </div>
                    {stripe?.plan_name && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Plan</span>
                        <span className="text-slate-700 font-medium">{stripe.plan_name}</span>
                      </div>
                    )}
                    {stripe?.mrr != null && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">MRR</span>
                        <span className="text-slate-700 font-medium">{fmt(stripe.mrr, stripe.currency || 'gbp')}</span>
                      </div>
                    )}
                    {stripe?.next_billing_date && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Next billing</span>
                        <span className="text-slate-700">{fmtDate(stripe.next_billing_date)}</span>
                      </div>
                    )}
                    {!stripe?.subscription_status && (
                      <div className="text-xs text-slate-400">No active subscription</div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* ── Conversation List ───────────────────────────────── */}
      <div className="w-72 shrink-0 border-r border-slate-200 flex flex-col bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex gap-2 items-center flex-wrap">
          <span className="font-bold text-[0.95rem] flex-1 text-slate-800">Support</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs px-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)] bg-white"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={() => setUnreadOnly(u => !u)}
            className={cn(
              'text-xs px-2.5 py-1 rounded-md border transition-colors cursor-pointer',
              unreadOnly ? 'bg-[hsl(138,26%,46%)] text-white border-[hsl(138,26%,46%)]' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            )}
          >Unread</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="px-4 py-6 text-slate-400 text-sm">Loading...</div>}
          {!loading && convs.length === 0 && <div className="px-4 py-6 text-slate-300 text-sm">No conversations</div>}
          {convs.map(c => (
            <div
              key={c.id}
              onClick={() => { setSelected(c); setShowDetails(false); }}
              className={cn(
                'px-4 py-3.5 border-b border-slate-50 cursor-pointer transition-colors border-l-2',
                selected?.id === c.id ? 'bg-[hsl(138,26%,97%)]' : 'bg-white hover:bg-slate-50',
                c.unread_admin > 0 ? 'border-l-[hsl(138,26%,46%)]' : 'border-l-transparent'
              )}
            >
              <div className="flex justify-between mb-0.5">
                <span className={cn('text-sm text-slate-800', c.unread_admin > 0 ? 'font-bold' : 'font-medium')}>{c.user_name}</span>
                <span className="text-[0.7rem] text-slate-300">{timeAgo(c.updated_at || c.created_at)}</span>
              </div>
              <div className="text-xs text-slate-500 mb-1 truncate">{c.subject}</div>
              <div className="flex gap-1.5 items-center">
                <span className="text-[0.68rem] text-slate-300 truncate">{c.user_email}</span>
                {c.org_name && <span className="text-[0.68rem] text-slate-300 shrink-0">· {c.org_name}</span>}
                {c.unread_admin > 0 && (
                  <span className="ml-auto bg-[hsl(138,26%,46%)] text-white rounded-full text-[0.65rem] px-1.5 font-bold shrink-0">{c.unread_admin}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Chat Thread ─────────────────────────────────────── */}
      {!selected ? (
        <div className="flex-1 flex items-center justify-center text-slate-300 text-sm">Select a conversation</div>
      ) : (
        <div className="flex-1 flex flex-col min-w-0">
          {/* Thread header */}
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-white">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 text-sm leading-tight">
                {selected.user_name}
                {selected.org_name && <span className="font-normal text-slate-400 text-xs ml-1.5">· {selected.org_name}</span>}
              </div>
              <div className="text-xs text-slate-400 truncate mt-0.5">{selected.subject}</div>
            </div>
            {/* View details button */}
            <button
              onClick={() => setShowDetails(v => !v)}
              title="View user & org details"
              className={cn(
                'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors cursor-pointer',
                showDetails
                  ? 'bg-[hsl(138,26%,94%)] text-[hsl(138,26%,32%)] border-[hsl(138,26%,75%)]'
                  : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
              )}
            >
              <svg width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>
              </svg>
              Details
            </button>
            <Btn size="sm" onClick={toggleStatus}
              className={selected.status === 'open'
                ? 'bg-green-50 text-[hsl(138,26%,40%)] border-green-200 hover:bg-green-100'
                : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'}
            >
              {selected.status === 'open' ? 'Close' : 'Reopen'}
            </Btn>
          </div>

          {/* Messages */}
          <div ref={chatRef} className="flex-1 overflow-y-auto p-5 flex flex-col gap-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center text-slate-300 text-sm py-8">No messages yet</div>
            )}
            {messages.map(m => (
              <div key={m.id} className={cn('flex gap-2.5 items-end', m.sender_type === 'admin' ? 'flex-row-reverse' : 'flex-row')}>
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                  m.sender_type === 'admin' ? 'bg-[hsl(138,26%,46%)] text-white' : 'bg-slate-200 text-slate-500'
                )}>
                  {m.sender_type === 'admin' ? 'N' : (m.sender_name?.[0] || 'U')}
                </div>
                <div className={cn(
                  'max-w-[70%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed',
                  m.sender_type === 'admin' ? 'bg-[hsl(138,26%,46%)] text-white' : 'bg-white text-slate-800 border border-slate-100'
                )}>
                  <div>{m.content}</div>
                  <div className={cn('text-[0.68rem] mt-1 opacity-60', m.sender_type === 'admin' ? 'text-right' : 'text-left')}>{timeAgo(m.created_at)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Reply input */}
          <div className="px-4 py-3 border-t border-slate-100 bg-white">
            <form onSubmit={sendReply} className="flex gap-2">
              <textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(e); } }}
                placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
                rows={2}
                className="flex-1 resize-none text-sm px-3 py-2 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)] focus:border-transparent placeholder:text-slate-400"
              />
              <Btn type="submit" variant="primary" size="sm" className="self-end" disabled={sending || !reply.trim()}>
                {sending ? <Spinner className="border-t-white" /> : 'Send'}
              </Btn>
            </form>
          </div>
        </div>
      )}

      {/* ── Detail Panel (slides in from right) ─────────────── */}
      {showDetails && <DetailPanel />}
    </div>
  );
}

// ── AI Insights Page ───────────────────────────────────────────────────────────
const FEATURE_COLOURS = {
  'assistant':    '#8b5cf6',
  'studio_image': '#f59e0b',
  'studio_ad':    '#ef4444',
  'studio_swap':  '#3b82f6',
  'studio_video': '#ec4899',
  'mmm_insights': '#10b981',
};
function featureColour(ep) {
  if (FEATURE_COLOURS[ep]) return FEATURE_COLOURS[ep];
  if (ep?.startsWith('studio_')) return '#f59e0b';
  return '#94a3b8';
}

function InsightsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api(`/api/admin/insights?days=${days}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  if (loading) return <Loader />;
  if (!data || data.error || !data.recent) return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
      <div className="text-sm font-medium mb-1">Failed to load AI insights</div>
      <div className="text-xs">{data?.error || 'Unknown error — check server logs'}</div>
    </div>
  );

  const { allTime, recent, topOrgs, orgBreakdown } = data;

  const totalTokensRecent  = recent.reduce((s, r) => s + Number(r.total_tokens), 0);
  const totalCostRecent    = recent.reduce((s, r) => s + geminiCost(r.prompt_tokens, r.output_tokens), 0);
  const totalCallsRecent   = recent.reduce((s, r) => s + Number(r.calls), 0);

  // Group orgBreakdown by org
  const orgMap = {};
  orgBreakdown.forEach(r => {
    if (!orgMap[r.org_id]) orgMap[r.org_id] = { org_name: r.org_name, org_id: r.org_id, features: [] };
    orgMap[r.org_id].features.push(r);
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-2xl font-bold tracking-tight">AI Insights</div>
          <div className="text-sm text-slate-400 mt-0.5">Platform-wide token usage and cost breakdown</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Period:</span>
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={cn('text-xs px-3 py-1.5 rounded-lg border transition-colors cursor-pointer',
                days === d ? 'bg-[hsl(138,26%,46%)] text-white border-[hsl(138,26%,46%)]' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
              )}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="grid gap-3.5 mb-8" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
        <StatCard label="Total Tokens" value={fmtN(totalTokensRecent)} sub={`Last ${days} days`} />
        <StatCard label="Total API Calls" value={fmtN(totalCallsRecent)} sub={`Last ${days} days`} />
        <StatCard label="Est. Cost" value={fmtCost(totalCostRecent)} sub="Gemini 2.5 Flash Lite" />
        <StatCard label="Orgs Active" value={topOrgs.length} sub={`Using AI in ${days}d`} />
      </div>

      {/* Feature breakdown — recent */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* By feature bar chart */}
        <Card className="p-5">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4">Token usage by feature (last {days}d)</div>
          {recent.length === 0 && <div className="text-slate-300 text-sm py-4 text-center">No data</div>}
          {recent.map(r => {
            const pct = totalTokensRecent > 0 ? (Number(r.total_tokens) / totalTokensRecent) * 100 : 0;
            const col = featureColour(r.endpoint);
            return (
              <div key={r.endpoint} className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col }} />
                    <span className="text-sm text-slate-700 font-medium">{r.label}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-slate-800">{fmtN(r.total_tokens)}</span>
                    <span className="text-xs text-slate-400 ml-2">{fmtCost(geminiCost(r.prompt_tokens, r.output_tokens))}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: col }} />
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{fmtN(r.calls)} calls · {pct.toFixed(1)}%</div>
              </div>
            );
          })}
        </Card>

        {/* All-time totals */}
        <Card className="p-5">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4">All-time totals by feature</div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left pb-2 text-xs text-slate-400 font-semibold uppercase tracking-wide">Feature</th>
                <th className="text-right pb-2 text-xs text-slate-400 font-semibold uppercase tracking-wide">Calls</th>
                <th className="text-right pb-2 text-xs text-slate-400 font-semibold uppercase tracking-wide">Tokens</th>
                <th className="text-right pb-2 text-xs text-slate-400 font-semibold uppercase tracking-wide">Cost</th>
              </tr>
            </thead>
            <tbody>
              {allTime.map(r => (
                <tr key={r.endpoint} className="border-b border-slate-50">
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: featureColour(r.endpoint) }} />
                      <span className="text-sm text-slate-700">{r.label}</span>
                    </div>
                  </td>
                  <td className="py-2 text-right text-sm text-slate-600">{fmtN(r.calls)}</td>
                  <td className="py-2 text-right text-sm font-semibold text-slate-800">{fmtN(r.total_tokens)}</td>
                  <td className="py-2 text-right text-sm font-semibold text-purple-600">{fmtCost(geminiCost(r.prompt_tokens, r.output_tokens))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Top orgs */}
      <Card className="overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">
          Top orgs by token usage — last {days}d
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Organisation</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Calls</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Tokens</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Est. cost</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Features used</th>
            </tr>
          </thead>
          <tbody>
            {topOrgs.map(o => {
              const features = (orgMap[o.org_id]?.features || []);
              return (
                <tr key={o.org_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-5 py-3 text-sm font-medium text-slate-800">{o.org_name || `Org ${o.org_id}`}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{fmtN(o.calls)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-800">{fmtN(o.total_tokens)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-purple-600">{fmtCost(geminiCost(o.prompt_tokens, o.output_tokens))}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {features.map(f => (
                        <span key={f.endpoint} className="inline-flex items-center gap-1 text-[0.67rem] px-1.5 py-0.5 rounded-full font-medium border"
                          style={{ background: featureColour(f.endpoint) + '18', color: featureColour(f.endpoint), borderColor: featureColour(f.endpoint) + '44' }}>
                          {f.label}
                          <span className="opacity-60">{fmtN(f.calls)}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
            {topOrgs.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-300 text-sm">No AI usage in this period</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <div className="text-xs text-slate-400 flex items-center gap-1.5">
        <svg width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
        Cost estimated at Gemini 2.5 Flash Lite rates: $0.075/1M input · $0.30/1M output. Studio uses gemini-3.1-flash-image-preview.
      </div>
    </div>
  );
}

// ── API Keys Page ─────────────────────────────────────────────────────────────
function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', service: 'gemini', key_value: '', max_orgs: 3 });
  const [revealed, setRevealed] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [assignOrg, setAssignOrg] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    Promise.all([
      api('/api/admin/api-keys').then(r => r.json()),
      api('/api/admin/orgs-list').then(r => r.json()),
    ]).then(([kd, od]) => {
      setKeys(kd.keys || []);
      setOrgs(od.orgs || []);
      setRefreshKey(n => n + 1);
      setLoading(false);
    }).catch(err => console.error('[ApiKeysPage] load error:', err));
  };

  useEffect(() => { load(); }, []);

  async function createKey(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.key_value.trim()) return;
    setCreating(true);
    const r = await api('/api/admin/api-keys', { method: 'POST', body: JSON.stringify(form) });
    if (r.ok) { setForm({ name: '', service: 'gemini', key_value: '', max_orgs: 3 }); load(); }
    setCreating(false);
  }

  async function revealKey(id) {
    if (revealed[id]) { setRevealed(r => ({ ...r, [id]: null })); return; }
    const r = await api(`/api/admin/api-keys/${id}/full`).then(x => x.json());
    setRevealed(rv => ({ ...rv, [id]: r.key_value }));
  }

  async function toggleActive(key) {
    await api(`/api/admin/api-keys/${key.id}`, { method: 'PUT', body: JSON.stringify({ is_active: key.is_active ? 0 : 1 }) });
    load();
  }

  async function deleteKey(id) {
    if (!confirm('Delete this API key? It will be unassigned from all orgs.')) return;
    await api(`/api/admin/api-keys/${id}`, { method: 'DELETE' });
    load();
  }

  async function assignOrgToKey(keyId) {
    const orgId = assignOrg[keyId];
    if (!orgId) return;
    const r = await api(`/api/admin/api-keys/${keyId}/orgs`, { method: 'POST', body: JSON.stringify({ org_id: orgId }) });
    if (!r.ok) { const d = await r.json(); alert(d.error); return; }
    load();
  }

  async function unassignOrg(keyId, orgId) {
    await api(`/api/admin/api-keys/${keyId}/orgs/${orgId}`, { method: 'DELETE' });
    load();
  }

  const svcColors = { gemini: '#4285f4', openai: '#10a37f' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-2xl font-bold tracking-tight">API Keys</div>
          <div className="text-sm text-slate-400 mt-0.5">Managed AI keys assigned to client orgs. One key can serve up to 3 orgs.</div>
        </div>
        <Btn onClick={async () => {
          if (!confirm('Import existing Gemini keys from org settings into this pool? Each key will be created and assigned to its org.')) return;
          const r = await api('/api/admin/api-keys/import-from-orgs', { method: 'POST' }).then(x => x.json());
          alert(`Imported ${r.imported} key(s).`);
          load();
        }}>Import from orgs</Btn>
      </div>

      {/* Create form */}
      <Card className="p-5 mb-6">
        <div className="font-semibold text-sm mb-3.5">Add new key</div>
        <form onSubmit={createKey} className="grid gap-2.5 items-end" style={{ gridTemplateColumns: '1fr auto 2fr auto auto' }}>
          <div>
            <label className="block text-[0.72rem] font-semibold text-slate-500 uppercase tracking-wider mb-1">Name</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Gemini Key A"
              className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
              required
            />
          </div>
          <div>
            <label className="block text-[0.72rem] font-semibold text-slate-500 uppercase tracking-wider mb-1">Service</label>
            <select
              value={form.service}
              onChange={e => setForm(f => ({ ...f, service: e.target.value }))}
              className="h-9 px-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)] bg-white"
            >
              <option value="gemini">Gemini</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>
          <div>
            <label className="block text-[0.72rem] font-semibold text-slate-500 uppercase tracking-wider mb-1">Key value</label>
            <input
              type="password"
              value={form.key_value}
              onChange={e => setForm(f => ({ ...f, key_value: e.target.value }))}
              placeholder="AIza..."
              className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
              required
            />
          </div>
          <div>
            <label className="block text-[0.72rem] font-semibold text-slate-500 uppercase tracking-wider mb-1">Max orgs</label>
            <input
              type="number"
              min={1}
              max={10}
              value={form.max_orgs}
              onChange={e => setForm(f => ({ ...f, max_orgs: parseInt(e.target.value) }))}
              className="h-9 w-16 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)]"
            />
          </div>
          <Btn type="submit" variant="primary" size="sm" disabled={creating}>
            {creating ? <Spinner className="border-t-white" /> : '+ Add key'}
          </Btn>
        </form>
      </Card>

      {/* Keys list */}
      {loading ? <Loader /> : (
        <div className="flex flex-col gap-3">
          {keys.length === 0 && <div className="text-slate-300 py-4">No API keys yet.</div>}
          {keys.map(k => (
            <Card key={k.id} className={cn('px-5 py-4', !k.is_active && 'opacity-55')} style={{ borderLeftWidth: 3, borderLeftColor: svcColors[k.service] || '#ccc' }}>
              <div className="flex gap-3 items-center">
                <span className="text-[0.68rem] font-bold uppercase px-1.5 py-0.5 rounded text-white" style={{ background: svcColors[k.service] || '#ccc' }}>{k.service}</span>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm">{k.name}</span>
                  <span className="ml-2.5 text-slate-300 text-xs font-mono">{revealed[k.id] ? revealed[k.id] : k.key_masked}</span>
                </div>
                <div className={cn('text-xs font-semibold', parseInt(k.orgs_count) >= k.max_orgs ? 'text-red-500' : 'text-emerald-600')}>
                  {k.orgs_count}/{k.max_orgs} orgs
                </div>
                <div className="flex gap-1.5">
                  <Btn size="xs" onClick={() => revealKey(k.id)}>{revealed[k.id] ? 'Hide' : 'Reveal'}</Btn>
                  <Btn size="xs" className={k.is_active ? 'text-amber-600' : 'text-emerald-600'} onClick={() => toggleActive(k)}>{k.is_active ? 'Disable' : 'Enable'}</Btn>
                  <Btn size="xs" onClick={() => setExpanded(expanded === k.id ? null : k.id)}>Orgs</Btn>
                  <Btn size="xs" variant="danger" onClick={() => deleteKey(k.id)}>Delete</Btn>
                </div>
              </div>

              {/* Expanded org management */}
              {expanded === k.id && (
                <div className="mt-3.5 pt-3.5 border-t border-slate-100">
                  <div className="flex gap-2 items-center mb-2.5">
                    <select
                      value={assignOrg[k.id] || ''}
                      onChange={e => setAssignOrg(a => ({ ...a, [k.id]: e.target.value }))}
                      className="flex-1 h-9 px-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(138,26%,46%)] bg-white"
                    >
                      <option value="">Select org to assign...</option>
                      {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <Btn variant="primary" size="sm" onClick={() => assignOrgToKey(k.id)} disabled={!assignOrg[k.id]}>Assign</Btn>
                  </div>
                  <KeyOrgList keyId={k.id} onUnassign={(orgId) => unassignOrg(k.id, orgId)} refreshKey={refreshKey} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function KeyOrgList({ keyId, onUnassign, refreshKey }) {
  const [orgs, setOrgs] = useState([]);
  useEffect(() => {
    api(`/api/admin/api-keys/${keyId}/orgs`).then(r => r.json()).then(d => setOrgs(d.orgs || []));
  }, [keyId, refreshKey]);
  if (orgs.length === 0) return <div className="text-slate-300 text-xs">No orgs assigned to this key.</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {orgs.map(o => (
        <span key={o.id} className="bg-slate-100 rounded-md px-2 py-0.5 text-xs flex items-center gap-1.5 text-slate-600">
          {o.name}
          <button onClick={() => onUnassign(o.id)} className="bg-transparent border-0 cursor-pointer text-red-400 hover:text-red-600 text-sm leading-none p-0">×</button>
        </span>
      ))}
    </div>
  );
}

// ── App Root ──────────────────────────────────────────────────────────────────
const VALID_PAGES = ['dashboard', 'clients', 'stripe', 'provision', 'support', 'apikeys', 'insights'];
function parseHash() {
  const hash = window.location.hash.replace('#', '');
  const parts = hash.split('/');
  const page = VALID_PAGES.includes(parts[0]) ? parts[0] : 'dashboard';
  const orgId = parts[0] === 'clients' && parts[1] ? parseInt(parts[1]) || null : null;
  const tab = parts[0] === 'clients' && parts[2] ? decodeURIComponent(parts[2]) : null;
  return { page, orgId, tab };
}
function getPageFromHash() { return parseHash().page; }

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(() => parseHash().page);
  const [checking, setChecking] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(() => parseHash().orgId);
  const [initialTab, setInitialTab] = useState(() => parseHash().tab);
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setChecking(false); return; }
    api('/api/admin/me').then(r => r.ok ? r.json() : null).then(u => {
      if (u) setUser(u);
      else localStorage.removeItem('admin_token');
      setChecking(false);
    }).catch(() => setChecking(false));
  }, []);

  // Poll unread support count + update page title
  useEffect(() => {
    if (!user) return;
    const poll = () => api('/api/admin/support/unread-count').then(r => r.json()).then(d => {
      const n = d.count || 0;
      setSupportUnread(n);
      document.title = n > 0 ? `(${n}) Nuso Admin` : 'Nuso Admin';
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [user]);

  // Sync hash → page on browser back/forward
  useEffect(() => {
    const handler = () => {
      const { page: p, orgId, tab } = parseHash();
      setPage(p);
      if (p === 'clients') { setSelectedOrgId(orgId); setInitialTab(tab); }
      else { setSelectedOrgId(null); setInitialTab(null); }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  function navigateTo(p) {
    window.location.hash = p;
    setPage(p);
  }

  function logout() {
    localStorage.removeItem('admin_token');
    setUser(null);
    window.location.hash = '';
  }

  if (checking) return (
    <div className="flex items-center justify-center h-screen w-full bg-slate-50">
      <Spinner className="w-6 h-6" />
    </div>
  );

  if (!user) return <LoginPage onLogin={u => { setUser(u); navigateTo(getPageFromHash()); }} />;

  return (
    <>
      <Sidebar page={page} setPage={navigateTo} user={user} onLogout={logout} supportUnread={supportUnread} />
      <main className={page === 'support' ? 'flex-1 flex flex-col overflow-hidden h-screen min-w-0' : 'flex-1 overflow-y-auto p-8 max-w-full'}>
        {page === 'dashboard' && <DashboardPage />}
        {page === 'clients' && !selectedOrgId && <ClientsPage onViewDetail={id => { setSelectedOrgId(id); setInitialTab('overview'); window.location.hash = `clients/${id}/overview`; }} />}
        {page === 'clients' && selectedOrgId && <OrgDetailPage orgId={selectedOrgId} initialTab={initialTab} onBack={() => { setSelectedOrgId(null); setInitialTab(null); window.location.hash = 'clients'; }} onTabChange={t => { setInitialTab(t); window.location.hash = `clients/${selectedOrgId}/${encodeURIComponent(t)}`; }} />}
        {page === 'stripe' && <StripePage />}
        {page === 'provision' && <ProvisionPage />}
        {page === 'support' && <SupportPage />}
        {page === 'insights' && <InsightsPage />}
        {page === 'apikeys' && <ApiKeysPage />}
      </main>
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
